// src/components/Entity/Components/EntityListSearch.tsx

import {
    TextField,
    InputAdornment,
    IconButton,
    Box,
    Select,
    MenuItem,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import React, { useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { IColumn, IGlobalSearchTextFilter } from '../interfaces'
import TruncatedText from '../../Common/TruncatedText'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { getEntityTableGlobalSearchTextFilter } from '../../../utils/EntityUtils'
import { setFilterList } from '../../../redux/slices/entitySlice'
import { ClearIcon } from '@mui/x-date-pickers'

interface EntityListSearchProps {
    // Redux/Automatic Mode Props
    templateId?: string
    applicationId?: string
    tabIndex?: string
    triggerSearch?: (searchText?: string) => void

    // Manual/Controlled Mode Props
    onSearchChange?: (value: string) => void
    onColumnSelect?: (col: any) => void
    searchableColumns: any[]
    selectedColumn?: any
    searchValue?: string
}

const EntityListSearch = ({
    templateId,
    applicationId,
    tabIndex,
    triggerSearch,
    // Manual props defaults
    onSearchChange,
    onColumnSelect,
    searchableColumns,
    selectedColumn: manualSelectedColumn,
    searchValue: manualSearchValue,
}: EntityListSearchProps): React.ReactElement => {
    // --- Determine Mode ---
    const isManualMode = !!onSearchChange

    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    // --- Redux Logic (Only runs if not manual, but hooks must run unconditionally) ---
    const allTableFilters = useAppSelector(
        (state) => state.entity.allTableFilters,
    )

    // Calculate Redux values (safe to calculate even if unused)
    const globalSearchTextInfo: IGlobalSearchTextFilter | undefined =
        templateId && !isManualMode
            ? getEntityTableGlobalSearchTextFilter(
                  allTableFilters,
                  templateId,
                  applicationId,
                  tabIndex,
              )
            : undefined

    // --- Effective State Values ---
    const currentSearchText = isManualMode
        ? manualSearchValue || ''
        : (globalSearchTextInfo as IGlobalSearchTextFilter)?.searchText || ''

    const currentSelectedColumn = isManualMode
        ? manualSelectedColumn
        : globalSearchTextInfo?.column

    const currentSearchableColumns = searchableColumns

    // --- Handlers ---
    const handleSearchTextChange = useCallback(
        (text: string) => {
            if (isManualMode && onSearchChange) {
                onSearchChange(text)
                return
            }

            // Redux Logic
            if (!templateId) return

            const updatedAllTableFilters = allTableFilters.map((filter) => {
                if (
                    filter.applicationId === applicationId &&
                    filter.tabIndex === tabIndex &&
                    filter.entityTemplateId === templateId
                ) {
                    return {
                        ...filter,
                        gloalSearchText: {
                            type: 'globalSearchText',
                            searchText: text,
                            column: filter.gloalSearchText?.column as IColumn,
                        } as IGlobalSearchTextFilter,
                    }
                }
                return filter
            })
            dispatch(setFilterList(updatedAllTableFilters))
            if (text === '') {
                setTimeout(() => triggerSearch?.(''), 100)
            }
        },
        [
            dispatch,
            allTableFilters,
            applicationId,
            tabIndex,
            templateId,
            isManualMode,
            onSearchChange,
            triggerSearch,
        ],
    )

    const handleSearchColumnChange = useCallback(
        (column: IColumn | null) => {
            if (isManualMode && onColumnSelect) {
                onColumnSelect(column)
                return
            }

            // Redux Logic
            if (!templateId) return

            const updatedAllTableFilters = allTableFilters.map((filter) => {
                if (
                    filter.applicationId === applicationId &&
                    filter.tabIndex === tabIndex &&
                    filter.entityTemplateId === templateId
                ) {
                    return {
                        ...filter,
                        gloalSearchText: {
                            type: 'globalSearchText',
                            searchText:
                                filter.gloalSearchText?.searchText ?? '',
                            column,
                        } as IGlobalSearchTextFilter,
                    }
                }
                return filter
            })
            dispatch(setFilterList(updatedAllTableFilters))
        },
        [
            dispatch,
            allTableFilters,
            applicationId,
            tabIndex,
            templateId,
            isManualMode,
            onColumnSelect,
        ],
    )

    const handleTriggerSearch = () => {
        if (triggerSearch) triggerSearch()
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'white',
                borderRadius: '25px',
            }}
        >
            <Box sx={{ width: '160px', borderRight: '2px solid #d3d3d3' }}>
                <Select
                    id="select-search-column"
                    value={currentSelectedColumn?.attributeName ?? ''}
                    onChange={(e) =>
                        handleSearchColumnChange(
                            currentSearchableColumns.find(
                                (col: any) =>
                                    col.attributeName === e.target.value,
                            ) ?? null,
                        )
                    }
                    renderValue={(selected) => {
                        if (!selected) {
                            return (
                                <TruncatedText
                                    style={{ fontSize: '14px', opacity: '0.4' }}
                                >
                                    Select
                                </TruncatedText>
                            )
                        }
                        return currentSearchableColumns.find(
                            (col: any) => col.attributeName === selected,
                        )?.columnName
                    }}
                    size="small"
                    sx={{
                        width: '100%',
                        fontSize: '14px',
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 250,
                                fontSize: '14px',
                            },
                        },
                    }}
                >
                    {currentSearchableColumns?.map((c: any) => (
                        <MenuItem
                            sx={{
                                fontSize: '14px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                            key={c.attributeName}
                            id={c.attributeName}
                            value={c.attributeName}
                        >
                            <TruncatedText
                                text={c.columnName}
                                sx={{
                                    fontSize: '14px',
                                }}
                            />
                        </MenuItem>
                    ))}
                </Select>
            </Box>
            <Box sx={{ width: '180px' }}>
                <TextField
                    id="common-search-input"
                    sx={{
                        padding: 0,
                        '& fieldset': { border: 'none' },
                        '& .MuiInputBase-root': { paddingLeft: '5px' },
                    }}
                    placeholder={t('search')}
                    value={currentSearchText}
                    onChange={(e) => handleSearchTextChange(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                            handleTriggerSearch()
                        }
                    }}
                    className="table-search-bar"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment
                                position="end"
                                sx={{ marginRight: '-8px' }}
                            >
                                {currentSearchText && (
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            handleSearchTextChange('')
                                        }
                                        sx={{
                                            visibility: 'hidden',
                                            '.Mui-focused &': {
                                                visibility: 'visible',
                                            },
                                            '.MuiInputBase-root:hover &': {
                                                visibility: 'visible',
                                            },
                                        }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <IconButton
                                    id="common-search-clear-button"
                                    sx={{
                                        '&:hover': { background: 'none' },
                                        padding: '6px',
                                    }}
                                    onClick={handleTriggerSearch}
                                >
                                    <SearchIcon sx={{ color: 'primary' }} />
                                </IconButton>
                            </InputAdornment>
                        ),
                        style: { height: 32 },
                    }}
                />
            </Box>
        </Box>
    )
}

const MemoizedEntityListSearch = memo(EntityListSearch)
MemoizedEntityListSearch.displayName = 'EntityListSearch'
export default MemoizedEntityListSearch
